/**
 * WHERE tab — README §1: 320px map, venue + address, GET DIRECTIONS / ADD TO CALENDAR,
 * header note for address visibility (EXACT ADDRESS SHOWN · ADDRESS AFTER GREENLIGHT ·
 * ADDRESS AFTER YOU ARE ACCEPTED · EXACT ADDRESS AFTER PURCHASE · PAST EVENT).
 */
import React, { useState } from 'react';
import { Panel, PanelHeader, SurfaceButton, MUTED, SUBTLE, SOFT } from '../surface';
import EventLocationMap from '../EventLocationMap';
import { MapPinIcon } from '../hand/icons';
import { googleCalendarUrl, downloadIcs, calendarPayloadForEvent } from './calendarLinks';

export function directionsUrl(event) {
  if (!event?.locationRevealed || !event?.locationAddress) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(event.locationAddress)}`;
}

export default function EventWhereTab({ event, note, mobile = false, pageUrl }) {
  const [calOpen, setCalOpen] = useState(false);
  const hasMap = !!(event?.locationLatitude && event?.locationLongitude);
  const dir = directionsUrl(event);
  const address = event?.locationRevealed
    ? (event.locationAddress || event.venueAddress || event.locationCity)
    : (event?.venueAddress || event?.locationCity || 'Address shared with confirmed guests');
  const venue = event?.venueName || (event?.locationRevealed ? event?.locationAddress : null) || event?.locationCity || 'LOCATION';
  const payload = calendarPayloadForEvent(event, pageUrl);
  const mapHeight = mobile ? 180 : 320;

  const map = hasMap ? (
    <EventLocationMap
      latitude={event.locationLatitude}
      longitude={event.locationLongitude}
      locationRevealed={!!event.locationRevealed}
      locationAddress={event.locationAddress}
      locationCity={event.locationCity}
      fundingType={event.fundingType}
      height={mapHeight}
      hideCaption
      bare
    />
  ) : (
    <div className="relative flex items-center justify-center" style={{ height: mapHeight, background: SOFT.chip }} data-testid="map-placeholder">
      <MapPinIcon size={34} />
      <span className="absolute font-mono font-bold uppercase" style={{ left: 14, bottom: 10, fontSize: 10, letterSpacing: '0.2em', color: SUBTLE }}>{event?.locationCity ? String(event.locationCity).toUpperCase() : 'MAP'}</span>
    </div>
  );

  const calendar = (
    <div className="flex flex-col gap-2">
      <SurfaceButton variant="outline" size={mobile ? 'tap' : 'sm'} onClick={() => setCalOpen((o) => !o)} aria-expanded={calOpen} data-testid="add-to-calendar">ADD TO CALENDAR</SurfaceButton>
      {calOpen && payload && (
        <div className="flex gap-2" data-testid="calendar-options">
          <SurfaceButton variant="outline" size="xs" onClick={() => window.open(googleCalendarUrl(payload), '_blank', 'noopener,noreferrer')}>GOOGLE</SurfaceButton>
          <SurfaceButton variant="outline" size="xs" onClick={() => downloadIcs(payload)}>APPLE · OUTLOOK (.ICS)</SurfaceButton>
        </div>
      )}
    </div>
  );

  const notePill = <span className="rounded-full font-mono font-bold uppercase" style={{ padding: '4px 10px', background: SOFT.chip, fontSize: 9, letterSpacing: '0.14em' }} data-testid="where-note">{note}</span>;

  if (mobile) {
    return (
      <Panel dense data-testid="where-tab">
        <PanelHeader label="WHERE" right={notePill} dense />
        <div className="overflow-hidden" style={{ borderRadius: 18 }}>{map}</div>
        <div>
          <div className="font-mono font-bold" style={{ fontSize: 14 }}>{venue}</div>
          <div className="font-mono" style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>{address}</div>
        </div>
        {dir && <SurfaceButton variant="outline" size="tap" onClick={() => window.open(dir, '_blank', 'noopener,noreferrer')} data-testid="get-directions">GET DIRECTIONS</SurfaceButton>}
        {calendar}
      </Panel>
    );
  }

  return (
    <Panel data-testid="where-tab">
      <PanelHeader label="WHERE" right={notePill} />
      <div className="font-mono font-bold" style={{ fontSize: 15 }}>{venue}</div>
      <div className="overflow-hidden" style={{ borderRadius: 20 }}>{map}</div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="font-mono" style={{ fontSize: 12, color: MUTED }}>{address}</div>
        <div className="flex items-start gap-2">
          {dir && <SurfaceButton variant="outline" size="sm" onClick={() => window.open(dir, '_blank', 'noopener,noreferrer')} data-testid="get-directions">GET DIRECTIONS</SurfaceButton>}
          {calendar}
        </div>
      </div>
      {!event?.locationRevealed && event?.status !== 'ended' && (
        <div className="font-mono" style={{ paddingTop: 12, borderTop: `2px solid ${SOFT.chip}`, fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
          {event?.fundingType === 'party_starter'
            ? 'The exact address is released the moment the host greenlights. Until then the map shows the neighbourhood.'
            : event?.mode === 'curated'
              ? 'The exact address is released once the host accepts you. Until then the map shows the neighbourhood.'
              : 'The exact address is released the moment you have a ticket. Until then the map shows the neighbourhood.'}
        </div>
      )}
    </Panel>
  );
}
